import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from './MainLayout';
import { 
  CircularProgress,
  Box,
  Typography,
  Pagination
} from '@mui/material';
import { SortablePaginatedTable } from './common';
import { formatDate, formatCurrency } from '../utils/dateUtils';

// Define column configurations for the table
const upcomingInvoiceTasksColumns = [
  { id: 'contract_number', label: 'Contract Number', sortable: true },
  { id: 'name', label: 'Name', sortable: true },
  { 
    id: 'due_date', 
    label: 'Due Date', 
    sortable: true,
    render: (row) => formatDate(row.due_date)
  },
  { 
    id: 'points', 
    label: 'Points', 
    sortable: true,
    align: 'right',
    render: (row) => row.points !== null && row.points !== undefined ? parseFloat(row.points).toFixed(2) : 'N/A'
  },
  { 
    id: 'invoice_amount', 
    label: 'Amount', 
    sortable: true,
    align: 'right',
    render: (row) => formatCurrency(row.invoice_amount)
  }
];

export const ForecastPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecastData, setForecastData] = useState({
    upcomingInvoiceTasks: [],
    pagination: {
      total: 0,
      page: 1,
      limit: 20,
      pages: 0
    }
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Create a dedicated function for fetching data
  const fetchForecastData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the API base URL
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      console.log('Using API base URL:', apiBaseUrl);
      
      // Fetch upcoming invoice tasks with pagination
      const response = await fetch(`${apiBaseUrl}/api/invoices/upcoming-tasks?page=${page}&limit=20`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch upcoming invoice tasks: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Upcoming invoice tasks data:', data);
      
      setForecastData({
        upcomingInvoiceTasks: data.data || [],
        pagination: data.pagination || {
          total: 0,
          page: 1,
          limit: 20,
          pages: 0
        }
      });
    } catch (error) {
      console.error('Failed to fetch forecast data:', error);
      setError('Failed to load forecast data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchForecastData(value);
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchForecastData(currentPage);
  }, [fetchForecastData, currentPage]);
  
  return (
    <MainLayout title="Forecast">
      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p className="error-details">
            Check the console for more details or contact your administrator.
          </p>
        </div>
      ) : (
        <div className="scrollable-content">
          <div className="tables-container">
            {/* Upcoming Invoice Tasks Table */}
            <div className="table-section">
              <h2 className="table-title">Upcoming Invoice Tasks</h2>
              {forecastData.upcomingInvoiceTasks.length === 0 ? (
                <div className="table-empty-container">
                  <p className="table-empty">No upcoming invoice tasks</p>
                </div>
              ) : (
                <>
                  <SortablePaginatedTable
                    data={forecastData.upcomingInvoiceTasks}
                    columns={upcomingInvoiceTasksColumns}
                    // Using internal pagination for the table
                    rowsPerPage={forecastData.pagination.limit}
                    emptyMessage="No upcoming invoice tasks"
                    tableContainerProps={{ className: "enhanced-table" }}
                    isContractDetailTable={false}
                    // Disable table's internal pagination since we're using API pagination
                    paginationProps={{ style: { display: 'none' } }}
                  />
                  
                  {/* API-based pagination */}
                  {forecastData.pagination.pages > 1 && (
                    <Box display="flex" justifyContent="center" my={2}>
                      <Pagination
                        count={forecastData.pagination.pages}
                        page={forecastData.pagination.page}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}; 